import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useBrandContext } from "../context/BrandContext";
import BrandSelector from "./BrandSelector";

interface NavLinkSpec {
  // For BRAND_SCOPED_LINKS: suffix appended to /{brand}.
  // For BRAND_AGNOSTIC_LINKS: the full path (no brand prefix).
  suffix: string;
  // Label rendered in the nav.
  label: string;
  // Whether to use exact-match for the active state.
  end?: boolean;
}

// Cross-brand views that live at top-level paths (no /{brand}/ prefix).
// Visible regardless of the BrandSelector value.
const BRAND_AGNOSTIC_LINKS: NavLinkSpec[] = [
  { suffix: "/analytics/stock-turn", label: "Stock turn" },
  { suffix: "/analytics/efficiency", label: "Tech score" },
  { suffix: "/analytics/price-spread", label: "Price spread" },
  { suffix: "/analytics/markdown-curve", label: "Markdown curve" },
  { suffix: "/analytics/price-changes", label: "Price changes" },
  { suffix: "/analytics/floor-price", label: "Floor price" },
  { suffix: "/analytics/range-spread", label: "Range spread" },
  { suffix: "/analytics/efficiency-vs-price", label: "Price vs efficiency" },
  {
    suffix: "/analytics/efficiency-vs-price-by-variant",
    label: "Price vs efficiency — by variant",
  },
];

// Brand-scoped views — paths are prefixed with /{activeBrand} at render time.
const BRAND_SCOPED_LINKS: NavLinkSpec[] = [
  { suffix: "/cars", label: "Inventory", end: true },
  { suffix: "/analytics/depreciation", label: "Depreciation" },
  { suffix: "/analytics/scatter", label: "Price vs mileage" },
  { suffix: "/analytics/price-vs-age", label: "Price vs age" },
  { suffix: "/analytics/time-on-lot", label: "Time on lot" },
  // Path stays `colour-mix`; the page now carries interior trim as well.
  { suffix: "/analytics/colour-mix", label: "Colour & trim mix" },
  { suffix: "/analytics/range-vs-price", label: "Price vs range" },
  { suffix: "/analytics/range-vs-price-by-variant", label: "Range by variant" },
  { suffix: "/analytics/floor-price", label: "Floor price" },
  { suffix: "/best-value", label: "Best value" },
];

const navClass = ({ isActive }: { isActive: boolean }) =>
  `px-3 py-1 rounded whitespace-nowrap shrink-0 ${
    isActive
      ? "bg-blue-100 text-blue-700"
      : "text-gray-700 hover:bg-gray-100"
  }`;

// Eyebrow caption above each tab cluster, so the viewer can see which
// tabs follow the BrandSelector and which are cross-brand.
const groupLabelClass =
  "px-3 pb-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400";

export default function NavBar() {
  const { user } = useAuth();
  const { brands, activeBrand } = useBrandContext();
  // Preserve the current `?…` query string across tab switches so the
  // filter sidebar's selections (models / variants / trims / capability
  // chips / etc.) survive navigation between Inventory and the analytics
  // tabs. Each page reads its filter from URL via `utils/filterUrl`.
  const { search } = useLocation();

  const activeBrandName =
    brands.find((b) => b.brand_code === activeBrand)?.display_name ??
    activeBrand;

  return (
    <nav className="border-b bg-white px-4 sm:px-6 py-2 flex flex-col gap-2 text-sm">
      {/* Row 1: brand selector + brand-scoped views. */}
      <div className="flex items-center gap-2 overflow-x-auto">
        <span className="font-semibold mr-2 shrink-0">EV Tracker</span>
        <div className="shrink-0">
          <BrandSelector />
        </div>

        {/* Brand-scoped views — these tabs follow the BrandSelector. */}
        <div
          className="flex flex-col shrink-0"
          role="group"
          aria-label="Selected brand"
        >
          <span className={groupLabelClass}>{activeBrandName}</span>
          <div className="flex items-center gap-1">
            {BRAND_SCOPED_LINKS.map(({ suffix, label, end }) => (
              <NavLink
                key={suffix}
                to={`/${activeBrand}${suffix}${search}`}
                end={end}
                className={navClass}
              >
                {label}
              </NavLink>
            ))}
          </div>
        </div>

        <div className="ml-auto flex items-center gap-2 shrink-0 pl-2">
          {user ? (
            <NavLink
              to="/filters"
              className={({ isActive }) =>
                `px-3 py-1 rounded whitespace-nowrap ${
                  isActive
                    ? "bg-blue-100 text-blue-700"
                    : "text-gray-700 hover:bg-gray-100"
                }`
              }
            >
              Saved filters
            </NavLink>
          ) : (
            <NavLink
              to="/login"
              className="px-3 py-1 rounded whitespace-nowrap text-gray-700 hover:bg-gray-100"
            >
              Log in
            </NavLink>
          )}
        </div>
      </div>

      {/* Row 2: cross-brand views — independent of the BrandSelector. */}
      <div className="flex items-center gap-2 overflow-x-auto">
        <div
          className="flex flex-col shrink-0"
          role="group"
          aria-label="All brands"
        >
          <span className={groupLabelClass}>All brands</span>
          <div className="flex items-center gap-1">
            {BRAND_AGNOSTIC_LINKS.map(({ suffix, label, end }) => (
              <NavLink
                key={suffix}
                to={`${suffix}${search}`}
                end={end}
                className={navClass}
              >
                {label}
              </NavLink>
            ))}
          </div>
        </div>
      </div>
    </nav>
  );
}
